import { useState } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownRight, ArrowUpRight, Info, Landmark } from 'lucide-react';
import { api } from '@/lib/api';
import { AppShell } from '@/components/app-shell';
import { useTenantMe } from '@/hooks/use-tenant-me';
import { useSetSnapshot } from '@/hooks/use-set-snapshot';
import { SetPresentacionesCard } from '@/components/set-presentaciones-card';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MonthlyBarChart } from '@/components/monthly-bar-chart';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription } from '@/components/ui/empty';

interface MonthRow {
  month: number;
  ingresos: number;
  egresos: number;
  ingresosCount: number;
  egresosCount: number;
  aPagar: number;
  ivaDebito: number;
  ivaCredito: number;
  ivaNeto: number;
}

const MONTH_NAMES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

function formatGs(n: number): string {
  return `Gs ${Math.round(Math.abs(n)).toLocaleString('es-PY')}`;
}

export function AppIvaListPage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading: loadingMe } = useTenantMe();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  const { data, isLoading } = useQuery({
    queryKey: ['app-income-expense', year],
    queryFn: () =>
      api<{ year: number; months: MonthRow[]; acumuladoFinal: number }>(
        `/api/app/income-expense?year=${year}`
      ),
    enabled: !!me,
  });

  // Saldo a favor OFICIAL de SET (acumulado). Independiente del año/facturas.
  const { data: setData } = useSetSnapshot();
  const setSnap = setData?.snapshot ?? null;
  const saldoOficial =
    setSnap && setSnap.status !== 'error' && setSnap.saldoFavor != null ? Number(setSnap.saldoFavor) : null;

  if (loadingMe) return <PageLoader />;
  if (!me) {
    navigate('/app/login');
    return null;
  }

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const months = data?.months ?? [];
  const active = months.filter((m) => m.ingresosCount > 0 || m.egresosCount > 0);
  const hasData = active.length > 0;

  // Mes "foco": el último mes del año con movimientos (o el mes actual).
  const focusMonth = hasData ? active[active.length - 1]! : null;

  const yearDebito = months.reduce((a, b) => a + b.ivaDebito, 0);
  const yearCredito = months.reduce((a, b) => a + b.ivaCredito, 0);
  const yearNeto = yearDebito - yearCredito;

  return (
    <AppShell>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Presentaciones de IVA</h1>
          <p className="text-sm text-muted-foreground">
            Tu IVA mes a mes. La declaración la presenta tu contador.
          </p>
        </div>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Saldo a favor acumulado OFICIAL de SET — el más autoritativo. */}
      {saldoOficial != null && saldoOficial > 0 && (
        <Card>
          <CardContent className="flex items-center justify-between gap-3 py-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Landmark className="size-4" />
              Saldo a favor acumulado (SET)
            </div>
            <div className="text-2xl font-bold tabular-nums text-success">{formatGs(saldoOficial)}</div>
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {data && !hasData && (
        <Card>
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Info />
              </EmptyMedia>
              <EmptyTitle>Sin movimientos en {year}</EmptyTitle>
              <EmptyDescription>
                Cuando cargues comprobantes de este año, vas a ver acá tu IVA mes a mes.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </Card>
      )}

      {data && hasData && (
        <>
          {/* Hero: IVA del mes foco (mismo layout que el de /app/renta) */}
          <Card>
            <CardContent className="grid gap-5 md:grid-cols-[1.4fr_1fr_1fr] md:gap-0">
              <div className="md:pr-6">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {focusMonth ? `IVA de ${MONTH_NAMES[focusMonth.month - 1]}` : 'IVA del mes'}
                </div>
                {focusMonth && focusMonth.ivaNeto >= 0 ? (
                  <>
                    <div className="mt-1 text-3xl font-bold tracking-tight text-primary">
                      {formatGs(focusMonth.ivaNeto)}
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">A pagar (estimado)</div>
                  </>
                ) : (
                  <>
                    <div className="mt-1 text-3xl font-bold tracking-tight text-success">
                      {formatGs(focusMonth?.ivaNeto ?? 0)}
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">Saldo a favor</div>
                  </>
                )}
              </div>
              <div className="md:border-l md:border-border md:pl-6">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <ArrowUpRight className="size-3.5 text-success" />
                  Ingresos
                </div>
                <div className="mt-1 text-xl font-semibold">
                  {formatGs(focusMonth?.ingresos ?? 0)}
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  IVA débito: {formatGs(focusMonth?.ivaDebito ?? 0)}
                </div>
              </div>
              <div className="md:border-l md:border-border md:pl-6">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <ArrowDownRight className="size-3.5 text-muted-foreground" />
                  Egresos
                </div>
                <div className="mt-1 text-xl font-semibold">
                  {formatGs(focusMonth?.egresos ?? 0)}
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  IVA crédito: {formatGs(focusMonth?.ivaCredito ?? 0)}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Chart: ingresos vs egresos brutos del mes (coherente con la tabla
              y con el chart de Renta). El IVA débito/crédito vive en la tabla. */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Ingresos vs egresos — {year}</CardTitle>
              <div className="text-right text-sm">
                <div className="text-xs text-muted-foreground">IVA neto del año</div>
                <div
                  className={
                    'font-semibold ' + (yearNeto >= 0 ? 'text-foreground' : 'text-success')
                  }
                >
                  {yearNeto >= 0 ? formatGs(yearNeto) : `${formatGs(yearNeto)} a favor`}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <MonthlyBarChart
                data={months.map((m) => ({
                  month: m.month,
                  values: { ingresos: m.ingresos, egresos: m.egresos },
                }))}
                series={[
                  { key: 'ingresos', label: 'Ingresos', colorClass: 'bg-success' },
                  { key: 'egresos', label: 'Egresos', colorClass: 'bg-muted-foreground/40' },
                ]}
                formatValue={formatGs}
                highlightMonth={focusMonth?.month}
              />
            </CardContent>
          </Card>

          {/* Tabla densa: totales brutos del mes (ingresos/egresos) + IVA derivado.
              Los brutos coinciden con los del portal SET; el IVA débito/crédito
              es el cálculo de Actuo (10% sobre estimadas, desglose real cuando hay). */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mes</TableHead>
                    <TableHead className="text-right">Ingresos</TableHead>
                    <TableHead className="text-right">Egresos</TableHead>
                    <TableHead className="text-right">Débito</TableHead>
                    <TableHead className="text-right">Crédito</TableHead>
                    <TableHead className="text-right">A pagar IVA</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...active].reverse().map((m) => (
                    <TableRow key={m.month}>
                      <TableCell className="font-medium">{MONTH_NAMES[m.month - 1]}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatGs(m.ingresos)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatGs(m.egresos)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {formatGs(m.ivaDebito)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {formatGs(m.ivaCredito)}
                      </TableCell>
                      <TableCell
                        className={
                          'text-right font-semibold tabular-nums ' +
                          (m.ivaNeto < 0 ? 'text-success' : '')
                        }
                      >
                        {m.ivaNeto >= 0 ? formatGs(m.ivaNeto) : `-${formatGs(m.ivaNeto)} a favor`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Info className="size-3.5" />
            El IVA a pagar es una estimación según tus facturas validadas. Tu contador presenta la
            declaración oficial a la SET.
          </p>
        </>
      )}

      {/* Últimas presentaciones de IVA (F.120) registradas en SET. */}
      <SetPresentacionesCard formularios={[120]} title="Últimas presentaciones de IVA" />
    </AppShell>
  );
}
